import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import jsPDF from "jspdf";
import { toPng } from "html-to-image";
import type { QRConfig } from "@/pages/generator/page";
import type { QRTemplate } from "./TemplateSelector";
import { useQRCodes } from "@/hooks/useQRCodes";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const REDIRECT_BASE = "https://getxohype.com/r";

interface Props {
  config: QRConfig;
}

/* ============================================================
   TEMPLATE RENDERERS
   ============================================================ */

interface TemplateProps {
  qrImageUrl: string;
  fgColor: string;
  bgColor: string;
  name: string;
  subtext: string;
  brand: string;
}

function ClassicTemplate({ qrImageUrl, fgColor, bgColor, name }: TemplateProps) {
  return (
    <div className="flex items-center justify-center w-full h-full" style={{ backgroundColor: bgColor }}>
      <div className="flex flex-col items-center gap-3">
        <img src={qrImageUrl} alt="QR" className="rounded-lg" style={{ width: 220, height: 220 }} />
        {name && <p className="text-sm font-semibold" style={{ color: fgColor }}>{name}</p>}
      </div>
    </div>
  );
}

function ModernTemplate({ qrImageUrl, fgColor, bgColor, name, subtext, brand }: TemplateProps) {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full gap-4 p-6 rounded-2xl" style={{ backgroundColor: bgColor }}>
      <div className="w-full flex items-center justify-between">
        {brand && <span className="text-xs font-bold uppercase tracking-wider" style={{ color: fgColor }}>{brand}</span>}
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: fgColor }} />
      </div>
      <div className="flex flex-col items-center gap-3">
        <img src={qrImageUrl} alt="QR" className="rounded-lg" style={{ width: 220, height: 220 }} />
        {name && <p className="text-sm font-semibold" style={{ color: fgColor }}>{name}</p>}
        {subtext && <p className="text-xs" style={{ color: fgColor, opacity: 0.6 }}>{subtext}</p>}
      </div>
      <div className="w-full flex justify-center">
        <div className="w-12 h-0.5" style={{ backgroundColor: fgColor, opacity: 0.3 }} />
      </div>
    </div>
  );
}

function BusinessTemplate({ qrImageUrl, fgColor, bgColor, name, subtext, brand }: TemplateProps) {
  return (
    <div className="flex flex-col items-center w-full h-full gap-4 p-6 rounded-xl border" style={{ backgroundColor: bgColor, borderColor: `${fgColor}20` }}>
      <div className="w-full flex items-center justify-between border-b pb-3" style={{ borderColor: `${fgColor}20` }}>
        {brand && <span className="text-xs font-bold uppercase tracking-wider" style={{ color: fgColor }}>{brand}</span>}
        <div className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: fgColor }} />
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: fgColor, opacity: 0.5 }} />
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: fgColor, opacity: 0.25 }} />
        </div>
      </div>
      <div className="flex flex-col items-center gap-2 flex-1 justify-center">
        <img src={qrImageUrl} alt="QR" className="rounded-lg" style={{ width: 220, height: 220 }} />
      </div>
      <div className="w-full text-center border-t pt-3" style={{ borderColor: `${fgColor}20` }}>
        {name && <p className="text-sm font-semibold" style={{ color: fgColor }}>{name}</p>}
        {subtext && <p className="text-xs mt-1" style={{ color: fgColor, opacity: 0.6 }}>{subtext}</p>}
      </div>
    </div>
  );
}

function SocialTemplate({ qrImageUrl, fgColor, bgColor, name, subtext, brand }: TemplateProps) {
  return (
    <div
      className="flex flex-col items-center justify-center w-full h-full gap-3 p-5 rounded-2xl"
      style={{ background: `linear-gradient(135deg, ${bgColor} 0%, ${fgColor}15 100%)` }}
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${fgColor}15` }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={fgColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </div>
        {brand && <span className="text-sm font-bold" style={{ color: fgColor }}>{brand}</span>}
      </div>
      <div className="relative">
        <img src={qrImageUrl} alt="QR" className="rounded-lg" style={{ width: 220, height: 220 }} />
        <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-md" style={{ backgroundColor: fgColor }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
          </svg>
        </div>
      </div>
      {name && <p className="text-sm font-semibold mt-1" style={{ color: fgColor }}>{name}</p>}
      {subtext && <p className="text-xs" style={{ color: fgColor, opacity: 0.6 }}>{subtext}</p>}
    </div>
  );
}

function GradientTemplate({ qrImageUrl, fgColor, bgColor, name, subtext }: TemplateProps) {
  return (
    <div
      className="flex flex-col items-center justify-center w-full h-full gap-3 p-6 rounded-2xl"
      style={{ background: `linear-gradient(135deg, ${bgColor}, ${fgColor}30)` }}
    >
      <div className="relative">
        <div className="absolute -inset-3 rounded-2xl opacity-20 blur-lg" style={{ backgroundColor: fgColor }} />
        <div className="relative">
          <img src={qrImageUrl} alt="QR" className="rounded-lg" style={{ width: 220, height: 220 }} />
        </div>
      </div>
      {name && <p className="text-sm font-semibold" style={{ color: fgColor }}>{name}</p>}
      {subtext && <p className="text-xs" style={{ color: fgColor, opacity: 0.6 }}>{subtext}</p>}
    </div>
  );
}

function DarkTemplate({ qrImageUrl, name, subtext, brand }: TemplateProps) {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full gap-3 p-6 rounded-xl" style={{ backgroundColor: "#1a1a2e" }}>
      <div className="w-full flex justify-between items-center">
        {brand && <span className="text-xs font-bold uppercase tracking-wider text-white/80">{brand}</span>}
        <div className="w-2 h-2 rounded-full bg-emerald-400" />
      </div>
      <div className="flex flex-col items-center gap-3">
        <div className="p-3 rounded-xl" style={{ backgroundColor: "#ffffff10" }}>
          <img src={qrImageUrl} alt="QR" className="rounded-lg" style={{ width: 220, height: 220 }} />
        </div>
        {name && <p className="text-sm font-semibold text-white">{name}</p>}
        {subtext && <p className="text-xs text-white/50">{subtext}</p>}
      </div>
    </div>
  );
}

function RoundedTemplate({ qrImageUrl, fgColor, bgColor, name, subtext }: TemplateProps) {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full gap-3 p-5 rounded-3xl" style={{ backgroundColor: bgColor }}>
      <div className="p-4 rounded-full" style={{ backgroundColor: `${fgColor}08` }}>
        <div className="rounded-2xl overflow-hidden">
          <img src={qrImageUrl} alt="QR" style={{ width: 200, height: 200 }} />
        </div>
      </div>
      {name && <p className="text-sm font-semibold" style={{ color: fgColor }}>{name}</p>}
      {subtext && <p className="text-xs" style={{ color: fgColor, opacity: 0.6 }}>{subtext}</p>}
    </div>
  );
}

function MinimalTemplate({ qrImageUrl, fgColor, bgColor, name }: TemplateProps) {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full gap-2 p-5" style={{ backgroundColor: bgColor }}>
      <img src={qrImageUrl} alt="QR" className="rounded-sm" style={{ width: 220, height: 220 }} />
      {name && <p className="text-xs font-medium tracking-wide uppercase" style={{ color: fgColor, opacity: 0.7 }}>{name}</p>}
    </div>
  );
}

function NeonTemplate({ qrImageUrl, fgColor, name, subtext }: TemplateProps) {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full gap-3 p-5 rounded-xl" style={{ backgroundColor: "#0a0a0a" }}>
      <div className="relative">
        <div className="absolute -inset-2 rounded-lg blur-md opacity-40" style={{ backgroundColor: fgColor }} />
        <div className="relative p-2 rounded-lg" style={{ backgroundColor: "#0a0a0a", border: `2px solid ${fgColor}` }}>
          <img src={qrImageUrl} alt="QR" className="rounded-md" style={{ width: 210, height: 210 }} />
        </div>
      </div>
      {name && <p className="text-sm font-semibold" style={{ color: fgColor }}>{name}</p>}
      {subtext && <p className="text-xs" style={{ color: fgColor, opacity: 0.6 }}>{subtext}</p>}
    </div>
  );
}

function CardTemplate({ qrImageUrl, fgColor, bgColor, name, subtext, brand }: TemplateProps) {
  return (
    <div className="flex flex-col items-center w-full h-full gap-3 p-5 rounded-xl border" style={{ backgroundColor: bgColor, borderColor: `${fgColor}15` }}>
      <div className="w-full flex items-center justify-between mb-1">
        {brand && <span className="text-xs font-bold" style={{ color: fgColor }}>{brand}</span>}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={fgColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
        </svg>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <img src={qrImageUrl} alt="QR" className="rounded-lg" style={{ width: 220, height: 220 }} />
      </div>
      <div className="w-full text-center pt-2 border-t" style={{ borderColor: `${fgColor}15` }}>
        {name && <p className="text-sm font-semibold" style={{ color: fgColor }}>{name}</p>}
        {subtext && <p className="text-xs mt-0.5" style={{ color: fgColor, opacity: 0.5 }}>{subtext}</p>}
      </div>
    </div>
  );
}

function QRTemplateWrapper({ template, ...props }: TemplateProps & { template: QRTemplate }) {
  switch (template) {
    case "classic": return <ClassicTemplate {...props} />;
    case "modern": return <ModernTemplate {...props} />;
    case "business": return <BusinessTemplate {...props} />;
    case "social": return <SocialTemplate {...props} />;
    case "gradient": return <GradientTemplate {...props} />;
    case "dark": return <DarkTemplate {...props} />;
    case "rounded": return <RoundedTemplate {...props} />;
    case "minimal": return <MinimalTemplate {...props} />;
    case "neon": return <NeonTemplate {...props} />;
    case "card": return <CardTemplate {...props} />;
    default: return <ClassicTemplate {...props} />;
  }
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function QRPreviewPanel({ config }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [downloading, setDownloading] = useState(false);
  const [format, setFormat] = useState<"png" | "jpeg" | "svg" | "pdf">("png");
  const [saving, setSaving] = useState(false);
  const [saveModal, setSaveModal] = useState(false);
  const [qrName, setQrName] = useState("");
  const [qrDescription, setQrDescription] = useState("");
  const { save } = useQRCodes();
  const navigate = useNavigate();
  const [savedQR, setSavedQR] = useState<{ shortCode: string; name: string } | null>(null);
  const { user } = useAuthContext();

  // Content encoded into the QR
  const qrContent = savedQR
    ? `${REDIRECT_BASE}?code=${savedQR.shortCode}`
    : (config.content || " ");

  // Generate QR data URL on a hidden canvas
  useEffect(() => {
    if (!config.content) return;
    QRCode.toDataURL(qrContent, {
      width: 600,
      margin: 2,
      color: { dark: config.fgColor, light: config.bgColor },
      errorCorrectionLevel: config.errorLevel,
    })
      .then((url) => setQrDataUrl(url))
      .catch(() => {});
  }, [config.fgColor, config.bgColor, config.errorLevel, qrContent]);

  const handleDownload = async () => {
    if (format === "svg") {
      setDownloading(true);
      try {
        const svgStr = await QRCode.toString(config.content || " ", {
          type: "svg",
          color: { dark: config.fgColor, light: config.bgColor },
          errorCorrectionLevel: config.errorLevel,
        });
        const blob = new Blob([svgStr], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "qr-code.svg";
        a.click();
        URL.revokeObjectURL(url);
      } finally {
        setDownloading(false);
      }
      return;
    }

    if (!previewRef.current) return;
    setDownloading(true);
    try {
      if (format === "pdf") {
        const dataUrl = await toPng(previewRef.current, { quality: 1, pixelRatio: 2 });
        const pdf = new jsPDF({ unit: "mm", format: "a4" });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const imgWidth = 80;
        const x = (pageWidth - imgWidth) / 2;
        const y = 40;
        pdf.addImage(dataUrl, "PNG", x, y, imgWidth, imgWidth);
        pdf.setFontSize(16);
        pdf.text("QR Kod", pageWidth / 2, y + imgWidth + 15, { align: "center" });
        pdf.setFontSize(10);
        pdf.text(`Hedef: ${config.content}`, pageWidth / 2, y + imgWidth + 25, { align: "center" });
        pdf.save("qr-code.pdf");
      } else {
        const dataUrl = await toPng(previewRef.current, { quality: 0.95, pixelRatio: 2 });
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `qr-code.${format}`;
        a.click();
      }
    } catch (err) {
      toast.error("İndirme başarısız oldu");
    } finally {
      setDownloading(false);
    }
  };

  const handleSave = async () => {
    if (!qrName.trim()) {
      toast.error("Lütfen QR kod için bir ad girin.");
      return;
    }
    if (!config.content?.trim()) {
      toast.error("Lütfen geçerli bir içerik girin (Adım 2).");
      return;
    }
    setSaving(true);
    try {
      const saved = await save({
        name: qrName.trim(),
        description: qrDescription.trim() || undefined,
        type: config.type,
        targetUrl: config.content,
        fgColor: config.fgColor,
        bgColor: config.bgColor,
        size: config.size,
        errorLevel: config.errorLevel,
        template: config.template,
        qrName: config.qrName || undefined,
        qrSubtext: config.qrSubtext || undefined,
        brandText: config.brandText || undefined,
      });
      setSaving(false);
      setSaveModal(false);
      setQrName("");
      setQrDescription("");
      setSavedQR({ shortCode: saved.shortCode, name: saved.name });
      toast.success("QR kodu kaydedildi! Artık gerçek yönlendirme linki aktif.");
      setTimeout(() => navigate("/my-qr"), 1500);
    } catch (err) {
      setSaving(false);
      const msg = err instanceof Error ? err.message : "Bilinmiyor";
      console.error("[QRPreviewPanel] Kaydetme hatası:", err);
      toast.error("Kaydetme başarısız: " + msg, { duration: 5000 });
    }
  };

  return (
    <div className="sticky top-24">
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="text-sm font-bold text-gray-700 mb-4">Önizleme</h3>

        {/* Hidden canvas for QR generation */}
        <canvas ref={canvasRef} style={{ display: "none" }} />

        {/* Template Preview */}
        <div
          ref={previewRef}
          className="w-full aspect-square rounded-xl overflow-hidden mb-4 bg-gray-50"
        >
          {qrDataUrl ? (
            <QRTemplateWrapper
              template={config.template}
              qrImageUrl={qrDataUrl}
              fgColor={config.fgColor}
              bgColor={config.bgColor}
              name={config.qrName}
              subtext={config.qrSubtext}
              brand={config.brandText}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <i className="ri-loader-4-line animate-spin text-2xl"></i>
            </div>
          )}
        </div>

        {/* Format Selector */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-slate-500 mb-2">İndirme Formatı</label>
          <div className="grid grid-cols-4 gap-2">
            {(["png", "svg", "jpeg", "pdf"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFormat(f)}
                className={`py-2 rounded-lg text-xs font-bold uppercase transition-all cursor-pointer whitespace-nowrap ${
                  format === f
                    ? "bg-blue-600 text-white"
                    : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Save as Dynamic QR */}
        {!user && (
          <div className="mb-2 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5"></i>
            <span>Kaydetmek için <a href="/login" className="underline font-medium">giriş yapın</a>.</span>
          </div>
        )}
        <button
          onClick={() => setSaveModal(true)}
          disabled={!config.content}
          className="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all cursor-pointer mb-2"
        >
          <i className="ri-save-line"></i> Dinamik QR Olarak Kaydet
        </button>

        {/* Download Button */}
        <button
          onClick={handleDownload}
          disabled={downloading || !config.content || !qrDataUrl}
          className="w-full flex items-center justify-center gap-2 py-3 bg-white hover:bg-slate-50 border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700 font-semibold rounded-xl transition-all cursor-pointer"
        >
          {downloading ? (
            <><i className="ri-loader-4-line animate-spin"></i> İndiriliyor...</>
          ) : (
            <><i className="ri-download-2-line"></i> {format.toUpperCase()} İndir</>
          )}
        </button>

        {/* Info */}
        <div className="mt-4 space-y-2">
          {savedQR && (
            <div className="flex items-start gap-2 text-xs text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2">
              <i className="ri-check-double-line mt-0.5"></i>
              <span>Dinamik QR aktif. Hedef URL istediğin zaman değiştirilebilir.</span>
            </div>
          )}
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>Şablon</span>
            <span className="font-medium text-gray-600 capitalize">{config.template}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>Boyut</span>
            <span className="font-medium text-gray-600">{config.size}×{config.size}px</span>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>Hata Düzeltme</span>
            <span className="font-medium text-gray-600">{config.errorLevel}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>Tür</span>
            <span className="font-medium text-gray-600 capitalize">{config.type}</span>
          </div>
        </div>
      </div>

      {/* Save Modal */}
      {saveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900">QR Kodu Kaydet</h3>
              <button
                onClick={() => setSaveModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Kaydedilen QR kodun hedef URL&apos;sini daha sonra &quot;QR Kodlarım&quot; sayfasından değiştirebilirsin.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">QR Kod Adı</label>
              <input
                type="text"
                value={qrName}
                onChange={(e) => setQrName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                placeholder="Örn: Menü QR Kodu"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 transition-colors"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setSaveModal(false)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-full hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !qrName.trim()}
                className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}